/* ── PAG Account Configuration constants ────────────────────────────────
   Types, column metadata, and fallback mock data for the Account
   Configuration (Kanban board) screen.
   ──────────────────────────────────────────────────────────────────────── */

import type { RiskLevel } from "./pagConstants";

import type { SeverityColorMap } from "../../../../api/frameworksApi";


export type UnifiedColumn =
  | "IDENTIFIED"
  | "AVAILABLE"
  | "NON_PRIVILEGED"
  | "CRITICAL"
  | "HIGH"
  | "MEDIUM"
  | "LOW"
  | "UNASSIGNED";

export const UNIFIED_COLUMNS: UnifiedColumn[] = [
  "IDENTIFIED",
  "NON_PRIVILEGED",
  "CRITICAL",
  "HIGH",
  "MEDIUM",
  "LOW",
  "UNASSIGNED",
];

export const PRIVILEGED_COLUMNS: UnifiedColumn[] = [
  "CRITICAL",
  "HIGH",
  "MEDIUM",
  "LOW",
  "UNASSIGNED",
];

export const NON_PRIVILEGED_COLUMN: UnifiedColumn = "NON_PRIVILEGED";
export const IDENTIFIED_COLUMN: UnifiedColumn = "IDENTIFIED";

export const UNIFIED_COLUMN_META: Record<
  UnifiedColumn,
  {
    label: string;
    hint: string;
    description: string;
    chip: string;
    dot: string;
    ring: string;
    headerBg: string;
    color: string;
  }
> = {
  IDENTIFIED: {
    label: "Identified Groups",
    hint: "Identified groups and accounts waiting to be categorized",
    description: "Directory groups and accounts discovered across connected systems.",
    chip: "bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20",
    dot: "bg-blue-400",
    ring: "border-blue-500/30",
    headerBg: "bg-blue-500/10",
    color: "#3B82F6",
  },
  AVAILABLE: {
    label: "Identified Groups",
    hint: "Identified groups and accounts waiting to be categorized",
    description: "Directory groups and accounts discovered across connected systems.",
    chip: "bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20",
    dot: "bg-blue-400",
    ring: "border-blue-500/30",
    headerBg: "bg-blue-500/10",
    color: "#3B82F6",
  },
  NON_PRIVILEGED: {
    label: "Non-privileged",
    hint: "Drag groups and accounts here to mark them as non-privileged",
    description: "Standard user access and routine operational permissions without elevated rights.",
    chip: "bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20",
    dot: "bg-emerald-500",
    ring: "border-emerald-500/30",
    headerBg: "bg-emerald-500/10",
    color: "#10B981",
  },
  UNASSIGNED: {
    label: "Privileged Unassigned",
    hint: "Privileged groups and accounts pending risk level assignment.",
    description: "Privileged entities awaiting risk severity classification.",
    chip: "bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20",
    dot: "bg-blue-400",
    ring: "border-blue-500/30",
    headerBg: "bg-blue-500/10",
    color: "#60A5FA",
  },
  CRITICAL: {
    label: "Privileged Critical",
    hint: "Highest risk privileged access with full administrative control.",
    description: "Full administrative control with critical directory and system impact.",
    chip: "bg-red-500/10 text-red-400 ring-1 ring-red-500/20",
    dot: "bg-red-500",
    ring: "border-red-500/30",
    headerBg: "bg-red-500/10",
    color: "#EF4444",
  },
  HIGH: {
    label: "Privileged High",
    hint: "High risk privileged access with sensitive administrative capabilities.",
    description: "Broad administrative capabilities with access to sensitive systems.",
    chip: "bg-orange-500/10 text-orange-400 ring-1 ring-orange-500/20",
    dot: "bg-orange-500",
    ring: "border-orange-500/30",
    headerBg: "bg-orange-500/10",
    color: "#F97316",
  },
  MEDIUM: {
    label: "Privileged Medium",
    hint: "Medium risk privileged access with operational or configuration privileges.",
    description: "Operational and configuration access requiring elevated governance.",
    chip: "bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20",
    dot: "bg-amber-400",
    ring: "border-amber-500/30",
    headerBg: "bg-amber-500/10",
    color: "#FBBF24",
  },
  LOW: {
    label: "Privileged Low",
    hint: "Lower risk privileged access with limited or specialized administrative scope.",
    description: "Limited administrative scope and specialized maintenance privileges.",
    chip: "bg-cyan-500/10 text-cyan-400 ring-1 ring-cyan-500/20",
    dot: "bg-cyan-400",
    ring: "border-cyan-500/30",
    headerBg: "bg-cyan-500/10",
    color: "#22D3EE",
  },
};

export const getUnifiedColumnMeta = (
  column: UnifiedColumn,
  palette?: SeverityColorMap,
): {
  label: string;
  hint: string;
  description: string;
  chip: string;
  dot: string;
  ring: string;
  headerBg: string;
  color: string;
} => {
  const base = UNIFIED_COLUMN_META[column];
  if (!palette) return base;
  switch (column) {
    case "CRITICAL":
      return { ...base, color: palette.critical || base.color };
    case "HIGH":
      return { ...base, color: palette.high || base.color };
    case "MEDIUM":
      return { ...base, color: palette.medium || base.color };
    case "LOW":
      return { ...base, color: palette.low || base.color };
    case "UNASSIGNED":
      return { ...base, color: palette.notAvailable || base.color };
    case "AVAILABLE":
      return base;
    default:
      return base;
  }
};

export type UnifiedItemType = "GROUP" | "ACCOUNT";

export interface UnifiedGroup {
  id: string;
  itemType: UnifiedItemType;
  groupId: number;
  accountId?: number;
  sourceSystemId: number;
  sourceSystemName?: string;
  groupName: string;
  userName?: string;
  displayName?: string | null;
  email?: string | null;
  description: string | null;
  accountCount: number;
  accounts?: import("../services/pagApi").PagAccountItem[];
  groupScope?: string;
  groupCategory?: string;
  baselinePrivileged: boolean;
  baselineRisk: RiskLevel | null;
  baselineCategory?: "IDENTIFIED" | "NON_PRIVILEGED" | "PRIVILEGED";
}
